/**
 * Data loader — loads GeoJSON files from local data directory or remote URLs.
 *
 * Country polygon fills use the binary mesh pipeline (loaded separately via
 * binaryMeshLoader.js). This loader handles GeoJSON for borders, queries,
 * and non-country datasets.
 */

const cache = new Map();

/**
 * Load a GeoJSON file, with caching
 * @param {string} path - Relative path from the data directory
 * @returns {Promise<object>} Parsed GeoJSON
 */
export async function loadGeoJSON(path) {
  if (cache.has(path)) {
    return cache.get(path);
  }

  const url = `/data/${path}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load ${path}: HTTP ${response.status}`);
    }
    const data = await response.json();
    cache.set(path, data);
    return data;
  } catch (err) {
    console.error(`[Loader] Failed to load ${path}:`, err.message);
    throw err;
  }
}

/**
 * Datasets that only exist as Parquet on R2 (queried via DuckDB), not as
 * local GeoJSON. Some ops (filter/compare/show/choropleth) may be handed
 * one of these names when the DuckDB path fails or is unavailable. Rather
 * than throw "Unknown dataset", we transparently fall back to the
 * countries GeoJSON so the legacy render path still produces something.
 *
 * See: D1 telemetry "Unknown dataset: continent_summary" from queries like
 * "Which continent has the most people?" when DuckDB wasn't ready.
 */
export const DUCKDB_ONLY_DATASETS = new Set([
  'country_indicators',
  'country_indicators_latest',
  'country_summary',
  'continent_summary',
  'temporal_population',
  'admin_summary',
  'city_rankings',
]);

/**
 * Load a dataset by name and detail level
 */
export async function loadDataset(datasetKey, detail = 'low') {
  if (DUCKDB_ONLY_DATASETS.has(datasetKey)) {
    console.warn(`[Loader] Dataset "${datasetKey}" is DuckDB-only; falling back to countries GeoJSON`);
    datasetKey = 'countries';
  }

  const paths = {
    countries: {
      low: 'natural-earth/countries-110m.geojson',
      medium: 'natural-earth/countries-50m.geojson'
    },
    states_provinces: {
      medium: 'natural-earth/states-provinces-50m.geojson'
    },
    cities: {
      low: 'natural-earth/populated-places-simple.geojson',
      medium: 'natural-earth/populated-places-50m.geojson'
    },
    rivers: {
      low: 'natural-earth/rivers-110m.geojson',
      medium: 'natural-earth/rivers-50m.geojson'
    },
    mountains: {
      medium: 'natural-earth/mountains-10m.geojson'
    },
    deserts: {
      medium: 'natural-earth/deserts-10m.geojson'
    },
    glaciated: {
      medium: 'natural-earth/glaciated-10m.geojson'
    },
    reefs: {
      medium: 'natural-earth/reefs-10m.geojson'
    },
    lakes: {
      medium: 'natural-earth/lakes-50m.geojson'
    }
  };

  const datasetPaths = paths[datasetKey];
  if (!datasetPaths) throw new Error(`Unknown dataset: ${datasetKey}`);

  const path = datasetPaths[detail] || Object.values(datasetPaths)[0];
  return loadGeoJSON(path);
}

/**
 * Preload essential datasets for fast startup.
 */
export async function preloadEssentials(onProgress) {
  onProgress?.('Loading countries...', 0);
  let countries, cities;

  try {
    countries = await loadDataset('countries', 'low');
  } catch (err) {
    console.warn(`[Loader] Failed to preload countries:`, err.message);
  }

  onProgress?.('Loading cities...', 50);
  try {
    cities = await loadDataset('cities', 'low');
  } catch (err) {
    console.warn(`[Loader] Failed to preload cities:`, err.message);
  }

  onProgress?.('Ready', 100);
  return { countries, cities };
}

/**
 * Clear the data cache
 */
export function clearCache() {
  cache.clear();
}
